import { useState, useEffect } from 'react';
import { Score, difficultyLabels, categoryLabels } from '../../../mocks/scores';
import SoundCloudPlayer from './SoundCloudPlayer';

interface ScoreModalProps {
  score: Score | null;
  onClose: () => void;
}

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

export default function ScoreModal({ score, onClose }: ScoreModalProps) {
  const [showForm, setShowForm] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (score) {
      document.body.style.overflow = 'hidden';
      setShowForm(false);
      setFormStatus('idle');
      setName('');
      setEmail('');
      setMessage('');
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [score]);

  if (!score) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > 500) return;
    setFormStatus('sending');

    const params = new URLSearchParams();
    params.append('score_title', score.title);
    params.append('score_reading', score.titleReading);
    params.append('name', name);
    params.append('email', email);
    params.append('message', message);

    try {
      const res = await fetch('https://readdy.ai/api/form/d6t5o1tm9vk3c28i5ch0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const difficultyColors: Record<string, string> = {
    beginner: 'bg-emerald-50 text-emerald-700',
    intermediate: 'bg-amber-50 text-amber-700',
    advanced: 'bg-rose-50 text-rose-800',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-8"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      {/* Panel */}
      <div
        className="relative bg-white w-full md:max-w-3xl max-h-[92vh] md:max-h-[85vh] rounded-t-2xl md:rounded-lg overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-sumi/50 hover:text-sumi transition-colors cursor-pointer"
          aria-label="閉じる"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="grid md:grid-cols-2 gap-0">
          {/* Left: Image */}
          <div className="relative h-56 md:h-auto md:min-h-[400px] overflow-hidden rounded-t-2xl md:rounded-l-lg md:rounded-tr-none">
            <img
              src={score.imageUrl}
              alt={score.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sumi/60 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-sumi/20"></div>
          </div>

          {/* Right: Details */}
          <div className="p-6 md:p-8 flex flex-col gap-5">
            {!showForm ? (
              <>
                {/* Title */}
                <div>
                  <p className="font-sans text-xs text-sumi/40 tracking-widest uppercase mb-1">
                    {categoryLabels[score.category]}
                  </p>
                  <h2 className="font-serif text-2xl md:text-3xl text-sumi">{score.title}</h2>
                  <p className="font-sans text-sm text-sumi/50 tracking-wider mt-1">{score.titleReading}</p>
                </div>

                {/* Subtitle */}
                <p className="font-sans text-sm text-sumi/70">{score.subtitle}</p>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-washi rounded-md p-3">
                    <p className="font-sans text-xs text-sumi/40 mb-1">編成</p>
                    <p className="font-serif text-sm text-sumi leading-relaxed">
                      {score.instrumentation.join(' / ')}
                    </p>
                  </div>
                  <div className="bg-washi rounded-md p-3">
                    <p className="font-sans text-xs text-sumi/40 mb-1">難易度</p>
                    <span className={`inline-block font-sans text-xs px-2 py-0.5 rounded-sm whitespace-nowrap ${difficultyColors[score.difficulty]}`}>
                      {difficultyLabels[score.difficulty]}
                    </span>
                  </div>
                  <div className="bg-washi rounded-md p-3">
                    <p className="font-sans text-xs text-sumi/40 mb-1">ページ数</p>
                    <p className="font-sans text-sm text-sumi">{score.pages}ページ</p>
                  </div>
                  <div className="bg-washi rounded-md p-3">
                    <p className="font-sans text-xs text-sumi/40 mb-1">形式</p>
                    <p className="font-sans text-sm text-sumi">PDF（印刷可）</p>
                  </div>
                </div>

                {/* Description */}
                <p className="font-serif text-sm text-sumi/80 leading-loose">{score.description}</p>

                {/* Audio Sample */}
                {score.audioSample && (
                  <SoundCloudPlayer
                    sample={score.audioSample}
                    title={score.title}
                  />
                )}

                {/* Price + CTA */}
                <div className="mt-auto pt-2 border-t border-sumi/10">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-sans text-xs text-sumi/40">販売価格（税込）</span>
                    <span className="font-sans text-2xl font-semibold text-sumi">
                      ¥{score.price.toLocaleString()}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowForm(true)}
                    className="w-full bg-sumi text-white font-sans text-sm tracking-widest py-3 rounded-md hover:bg-sumi/80 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    購入お問い合わせ
                  </button>
                  <p className="font-sans text-xs text-sumi/40 text-center mt-2">
                    ご注文後、PDFをメールにてお送りします
                  </p>
                </div>
              </>
            ) : (
              /* Purchase Form */
              <div className="flex flex-col gap-4">
                <div>
                  <button
                    onClick={() => setShowForm(false)}
                    className="flex items-center gap-1 text-sumi/50 hover:text-sumi transition-colors cursor-pointer mb-3"
                  >
                    <i className="ri-arrow-left-line text-sm"></i>
                    <span className="font-sans text-xs">戻る</span>
                  </button>
                  <h3 className="font-serif text-xl text-sumi">購入お問い合わせ</h3>
                  <p className="font-sans text-xs text-sumi/50 mt-1">
                    「{score.title}」のご購入について
                  </p>
                </div>

                {formStatus === 'success' ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-4 py-8">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-50">
                      <i className="ri-check-line text-emerald-600 text-xl"></i>
                    </div>
                    <p className="font-serif text-base text-sumi text-center">
                      お問い合わせを受け付けました
                    </p>
                    <p className="font-sans text-xs text-sumi/60 text-center leading-relaxed">
                      ご登録のメールアドレスに<br />確認メールをお送りします
                    </p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div>
                      <label className="font-sans text-xs text-sumi/60 block mb-1">
                        お名前 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full border border-sumi/15 rounded-md px-3 py-2 font-sans text-sm text-sumi focus:outline-none focus:border-sumi/40 bg-white"
                        placeholder="山田 太郎"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-xs text-sumi/60 block mb-1">
                        メールアドレス <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full border border-sumi/15 rounded-md px-3 py-2 font-sans text-sm text-sumi focus:outline-none focus:border-sumi/40 bg-white"
                        placeholder="example@mail.com"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-xs text-sumi/60 block mb-1">
                        ご質問・ご要望（任意）
                        <span className="text-sumi/30 ml-2">{message.length}/500</span>
                      </label>
                      <textarea
                        name="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={500}
                        rows={3}
                        className="w-full border border-sumi/15 rounded-md px-3 py-2 font-sans text-sm text-sumi focus:outline-none focus:border-sumi/40 bg-white resize-none"
                        placeholder="ご質問があればお気軽にどうぞ"
                      />
                    </div>

                    {formStatus === 'error' && (
                      <p className="font-sans text-xs text-rose-600">
                        送信に失敗しました。再度お試しください。
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'sending' || message.length > 500}
                      className="w-full bg-sumi text-white font-sans text-sm tracking-widest py-3 rounded-md hover:bg-sumi/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {formStatus === 'sending' ? '送信中...' : '送信する'}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
